import {
  OrderValue as InterviewOrderValue,
  sortableColumns as interviewSortableColumns,
} from 'components/Interview/List';
import {
  customTagColumnNames as mainCustomTagColumnNames,
  CustomTags as MainCustomTags,
  OrderValue as MainOrderValue,
  sortableColumns as mainSortableColumns,
  tagColumnNames as mainTagColumns,
  TagColumnNames as MainTagColumns,
} from 'pages/admin/mentoring/mentors';
import {
  defaultExportColumns,
  ExportColumn,
  exportColumnNames,
  ExportColumns,
} from 'pages/admin/mentoring/mentors/Export';
import {
  columnNames as mainColumnNames,
  ColumnNames as MainColumnNames,
  Columns as MainColumns,
  initialActiveColumns as mainInitialActiveColumns,
} from 'pages/admin/mentoring/mentors/index-components/MentorsList';
import {
  OrderValue as AssociateOrderValue,
  sortableColumns as associateSortableColumns,
  tagColumnNames as associateTagColumns,
  TagColumnNames as AssociateTagColumns,
} from 'pages/admin/mentoring/mentors/[id]/Mentoring/Associate';
import {
  columnNames as associateColumnNames,
  ColumnNames as AssociateColumnNames,
  Columns as AssociateColumns,
  initialActiveColumns as associateInitialActiveColumns,
} from 'pages/admin/mentoring/mentors/[id]/Mentoring/Associate/AssociateList';
import {
  columnNames as historyColumnNames,
  ColumnNames as HistoryColumnNames,
  Columns as HistoryColumns,
  initialActiveColumns as historyInitialActiveColumns,
  OrderValue as HistoryOrderValue,
  sortableColumns as historySortableColumns,
  tagColumnNames as historyTagColumns,
  TagColumnNames as HistoryTagColumns,
} from 'pages/admin/mentoring/mentors/[id]/Mentoring/History';
import {
  columnNames as ongoingColumnNames,
  ColumnNames as OngoingColumnNames,
  Columns as OngoingColumns,
  initialActiveColumns as ongoingInitialActiveColumns,
  tagColumnNames as ongoingTagColumns,
  TagColumnNames as OngoingTagColumns,
} from 'pages/admin/mentoring/mentors/[id]/Mentoring/Ongoing';
import { List } from 'store/actions/admin/mentorLists';
import ActionTypes from 'store/actionTypes/admin/mentorLists';
import { ListState } from 'store/reducers/types';
import { updateObject } from 'store/updateObject';
import Tags from 'types/Tags';
import {
  isColumns,
  isNumberAction,
  isObjectAction,
  isOrderAction,
  isStringAction,
  isTagsAction,
  TypedAction,
} from 'types/typeguards/reducerActions';

export type MentorListsState = {
  main: ListState<
    MainTagColumns,
    MainColumns,
    MainOrderValue,
    MainCustomTags
  > & {
    exportColumns: Record<ExportColumn, boolean>;
  };
  associate: ListState<
    AssociateTagColumns,
    AssociateColumns,
    AssociateOrderValue
  >;
  history: ListState<HistoryTagColumns, HistoryColumns, HistoryOrderValue>;
  ongoing: ListState<OngoingTagColumns, OngoingColumns, 'asc/'>;
  interview: {
    page: number;
    order: InterviewOrderValue;
  };
};

const initialState: MentorListsState = {
  main: {
    page: 0,
    tags: {
      referee: [],
      status: [],
      jobSearch: [],
      careerPlan: [],
      studies: [],
      entrepreneurship: [],
      expertiseFields: [],
      formations: [],
      diploma: [],
      consideration: [],
      hobby: [],
      wantedMentorships: [],
    },
    customTags: {
      eventStartDate: '',
    },
    search: '',
    activeColumns: mainInitialActiveColumns,
    take: 100,
    order: 'desc/createdAt',
    exportColumns: defaultExportColumns,
  },
  associate: {
    page: 0,
    tags: {
      status: [],
      jobSearch: [],
      careerPlan: [],
      studies: [],
      entrepreneurship: [],
      expertiseFields: [],
      completedFormation: [],
      validatedDiploma: [],
      formationMatchingProject: [],
      formationState: [],
      intendedFormation: [],
      intendedDiploma: [],
      consideration: [],
      hobby: [],
      volunteerState: [],
    },
    search: '',
    activeColumns: associateInitialActiveColumns,
    take: 100,
    order: 'desc/matching',
  },
  history: {
    page: 0,
    tags: {
      status: [],
    },
    search: '',
    activeColumns: historyInitialActiveColumns,
    take: 100,
    order: 'desc/end',
  },
  ongoing: {
    page: 0,
    tags: {
      status: [],
    },
    search: '',
    activeColumns: ongoingInitialActiveColumns,
    take: 10,
    order: 'asc/',
  },
  interview: {
    page: 0,
    order: 'desc/date',
  },
};

const setPage = (
  list: List,
  state: MentorListsState,
  action: TypedAction<number>,
) => {
  return updateObject(state, {
    [list]: updateObject(state[list], {
      page: action.value,
    }),
  });
};

const setSearch = (
  list: List,
  state: MentorListsState,
  action: TypedAction<string>,
) => {
  return updateObject(state, {
    [list]: updateObject(state[list], {
      search: action.value,
      page: 0,
    }),
  });
};

const setMainTags = (
  state: MentorListsState,
  action: TypedAction<Tags<MainTagColumns>>,
) => {
  return updateObject(state, {
    main: updateObject(state.main, {
      tags: action.value,
      page: 0,
    }),
  });
};
const setAssociateTags = (
  state: MentorListsState,
  action: TypedAction<Tags<AssociateTagColumns>>,
) => {
  return updateObject(state, {
    associate: updateObject(state.associate, {
      tags: action.value,
      page: 0,
    }),
  });
};
const setHistoryTags = (
  state: MentorListsState,
  action: TypedAction<Tags<HistoryTagColumns>>,
) => {
  return updateObject(state, {
    history: updateObject(state.history, {
      tags: action.value,
      page: 0,
    }),
  });
};
const setOngoingTags = (
  state: MentorListsState,
  action: TypedAction<Tags<OngoingTagColumns>>,
) => {
  return updateObject(state, {
    ongoing: updateObject(state.ongoing, {
      tags: action.value,
      page: 0,
    }),
  });
};

const setMainCustomTags = (
  state: MentorListsState,
  action: TypedAction<MainCustomTags>,
) => {
  return updateObject(state, {
    main: updateObject(state.main, {
      customTags: action.value,
      page: 0,
    }),
  });
};

const setMainActiveColumns = (
  state: MentorListsState,
  action: TypedAction<MainColumns>,
) => {
  return updateObject(state, {
    main: updateObject(state.main, {
      activeColumns: action.value,
    }),
  });
};
const setAssociateActiveColumns = (
  state: MentorListsState,
  action: TypedAction<AssociateColumns>,
) => {
  return updateObject(state, {
    associate: updateObject(state.associate, {
      activeColumns: action.value,
    }),
  });
};
const setHistoryActiveColumns = (
  state: MentorListsState,
  action: TypedAction<HistoryColumns>,
) => {
  return updateObject(state, {
    history: updateObject(state.history, {
      activeColumns: action.value,
    }),
  });
};
const setOngoingActiveColumns = (
  state: MentorListsState,
  action: TypedAction<OngoingColumns>,
) => {
  return updateObject(state, {
    ongoing: updateObject(state.ongoing, {
      activeColumns: action.value,
    }),
  });
};

const setMainExportColumns = (
  state: MentorListsState,
  action: TypedAction<ExportColumns>,
) => {
  return updateObject(state, {
    main: updateObject(state.main, {
      exportColumns: action.value,
    }),
  });
};

const setTake = (
  list: List,
  state: MentorListsState,
  action: TypedAction<number>,
) => {
  return updateObject(state, {
    [list]: updateObject(state[list], {
      take: action.value,
      page: 0,
    }),
  });
};

const setOrderMain = (
  state: MentorListsState,
  action: TypedAction<MainOrderValue>,
) => {
  return updateObject(state, {
    main: updateObject(state.main, {
      order: action.value,
      page: 0,
    }),
  });
};
const setOrderAssociate = (
  state: MentorListsState,
  action: TypedAction<AssociateOrderValue>,
) => {
  return updateObject(state, {
    associate: updateObject(state.associate, {
      order: action.value,
      page: 0,
    }),
  });
};
const setOrderHistory = (
  state: MentorListsState,
  action: TypedAction<HistoryOrderValue>,
) => {
  return updateObject(state, {
    history: updateObject(state.history, {
      order: action.value,
      page: 0,
    }),
  });
};
const setOrderInterview = (
  state: MentorListsState,
  action: TypedAction<InterviewOrderValue>,
) => {
  return updateObject(state, {
    interview: updateObject(state.interview, {
      order: action.value,
      page: 0,
    }),
  });
};

const mentorListsReducer = (
  state = initialState,
  action: { type: string; value: unknown },
) => {
  switch (action.type) {
    // Main
    case ActionTypes.MAIN_SET_PAGE:
      if (isNumberAction(action)) {
        return setPage('main', state, action);
      }
      console.log('Action type error: setPage');
      return state;

    case ActionTypes.MAIN_SET_SEARCH:
      if (isStringAction(action)) {
        return setSearch('main', state, action);
      }
      console.log('Action type error: setSearch');
      return state;

    case ActionTypes.MAIN_SET_TAGS:
      if (isTagsAction<MainTagColumns>(mainTagColumns, action)) {
        return setMainTags(state, action);
      }
      console.log('Action type error: setTags');
      return state;

    case ActionTypes.MAIN_SET_CUSTOM_TAGS:
      if (isObjectAction<MainCustomTags>(mainCustomTagColumnNames, action)) {
        return setMainCustomTags(state, action as TypedAction<MainCustomTags>);
      }
      console.log('Action type error: setCustomTags');
      return state;

    case ActionTypes.MAIN_SET_ACTIVE_COLUMNS:
      if (isColumns<MainColumnNames>(mainColumnNames, action)) {
        return setMainActiveColumns(state, action);
      }
      console.log('Action type error: setActiveColumns');
      return state;

    case ActionTypes.MAIN_SET_EXPORT_COLUMNS:
      if (isColumns(exportColumnNames, action)) {
        return setMainExportColumns(state, action);
      }
      console.log('Action type error: setExportColumns');
      return state;

    case ActionTypes.MAIN_SET_TAKE:
      if (isNumberAction(action)) {
        return setTake('main', state, action);
      }
      console.log('Action type error: setTake');
      return state;

    case ActionTypes.MAIN_SET_ORDER:
      if (isOrderAction(mainSortableColumns, action)) {
        return setOrderMain(state, action);
      }
      console.log('Action type error: setTake');
      return state;

    // Associate
    case ActionTypes.ASSOCIATE_SET_PAGE:
      if (isNumberAction(action)) {
        return setPage('associate', state, action);
      }
      console.log('Action type error: setPage');
      return state;

    case ActionTypes.ASSOCIATE_SET_SEARCH:
      if (isStringAction(action)) {
        return setSearch('associate', state, action);
      }
      console.log('Action type error: setSearch');
      return state;

    case ActionTypes.ASSOCIATE_SET_TAGS:
      if (isTagsAction<AssociateTagColumns>(associateTagColumns, action)) {
        return setAssociateTags(state, action);
      }
      console.log('Action type error: setTags');
      return state;

    case ActionTypes.ASSOCIATE_SET_ACTIVE_COLUMNS:
      if (isColumns<AssociateColumnNames>(associateColumnNames, action)) {
        return setAssociateActiveColumns(state, action);
      }
      console.log('Action type error: setActiveColumns');
      return state;

    case ActionTypes.ASSOCIATE_SET_TAKE:
      if (isNumberAction(action)) {
        return setTake('associate', state, action);
      }
      console.log('Action type error: setTake');
      return state;

    case ActionTypes.ASSOCIATE_SET_ORDER:
      if (isOrderAction(associateSortableColumns, action)) {
        return setOrderAssociate(state, action);
      }
      console.log('Action type error: setTake');
      return state;

    // History
    case ActionTypes.HISTORY_SET_PAGE:
      if (isNumberAction(action)) {
        return setPage('history', state, action);
      }
      console.log('Action type error: setPage');
      return state;

    case ActionTypes.HISTORY_SET_SEARCH:
      if (isStringAction(action)) {
        return setSearch('history', state, action);
      }
      console.log('Action type error: setSearch');
      return state;

    case ActionTypes.HISTORY_SET_TAGS:
      if (isTagsAction<HistoryTagColumns>(historyTagColumns, action)) {
        return setHistoryTags(state, action);
      }
      console.log('Action type error: setTags');
      return state;

    case ActionTypes.HISTORY_SET_ACTIVE_COLUMNS:
      if (isColumns<HistoryColumnNames>(historyColumnNames, action)) {
        return setHistoryActiveColumns(state, action);
      }
      console.log('Action type error: setActiveColumns');
      return state;

    case ActionTypes.HISTORY_SET_TAKE:
      if (isNumberAction(action)) {
        return setTake('history', state, action);
      }
      console.log('Action type error: setTake');
      return state;

    // Ongoing
    case ActionTypes.ONGOING_SET_PAGE:
      if (isNumberAction(action)) {
        return setPage('ongoing', state, action);
      }
      console.log('Action type error: setPage');
      return state;

    case ActionTypes.ONGOING_SET_SEARCH:
      if (isStringAction(action)) {
        return setSearch('ongoing', state, action);
      }
      console.log('Action type error: setSearch');
      return state;

    case ActionTypes.ONGOING_SET_TAGS:
      if (isTagsAction<OngoingTagColumns>(ongoingTagColumns, action)) {
        return setOngoingTags(state, action);
      }
      console.log('Action type error: setTags');
      return state;

    case ActionTypes.ONGOING_SET_ACTIVE_COLUMNS:
      if (isColumns<OngoingColumnNames>(ongoingColumnNames, action)) {
        return setOngoingActiveColumns(state, action);
      }
      console.log('Action type error: setActiveColumns');
      return state;

    case ActionTypes.HISTORY_SET_ORDER:
      if (isOrderAction(historySortableColumns, action)) {
        return setOrderHistory(state, action);
      }
      console.log('Action type error: setTake');
      return state;

    // Interview
    case ActionTypes.INTERVIEW_SET_PAGE:
      if (isNumberAction(action)) {
        return setPage('interview', state, action);
      }
      console.log('Action type error: setPage');
      return state;

    case ActionTypes.INTERVIEW_SET_ORDER:
      if (isOrderAction(interviewSortableColumns, action)) {
        return setOrderInterview(state, action);
      }
      console.log('Action type error: setTake');
      return state;

    default:
      return state;
  }
};

export default mentorListsReducer;
